import { Component, OnInit, ElementRef, inject, signal, computed, Input, HostListener, OnDestroy, ChangeDetectorRef, effect, input, output, viewChild, DestroyRef, runInInjectionContext, Injector, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm, FormControl, Validators, FormGroup, FormBuilder } from '@angular/forms';
import { WebcamComponent } from '@shared/components/webcam/webcam.component';
import { Firestore, collection, addDoc, doc, deleteDoc, getDoc, updateDoc, setDoc, DocumentReference, Timestamp, deleteField, CollectionReference, onSnapshot, query, getDocs, orderBy, limit, where } from '@angular/fire/firestore';
import { Storage, ref, uploadBytes, getDownloadURL, deleteObject, getStorage, listAll, getMetadata } from '@angular/fire/storage';
import { UserService } from '@services/user.service';
import { Video, VideoStatus } from '@shared/models/video.model';
import { ToastService } from '@shared/services/toast.service';
import { HttpClientModule } from '@angular/common/http';
import { MailgunService } from '@shared/services/mailgun.service';
import { TimestampToDatePipe } from '@shared/pipes/timestamp-to-date.pipe';
import { VideoEditComponent } from '@shared/components/videoedit/videoedit.component';
import { StorageService } from '@shared/services/storage.service';
import { EmailService } from '@shared/services/email.service';
import { RecordingService } from '@services/recording.service';
import { DynamicField } from '@shared/models/dynamic-field.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { DeleteRunwayService } from '@shared/services/delete-runway.service';
import { VideoTrimService } from '@shared/services/video-trim.service';
import { environment } from '@environments/environment';
import { Subscription } from 'rxjs';


@Component({
  selector: 'app-video-details',
  templateUrl: './video-details.component.html',
  styleUrls: ['./video-details.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    WebcamComponent,    
    TimestampToDatePipe,
    VideoEditComponent
  ],
  providers: [MailgunService]
})
export class VideoDetailsComponent implements OnInit, OnDestroy {
  readonly closeModalEvent = output<void>();
  readonly attemptCloseWithUnsavedChanges = output<boolean>();
  readonly videoForm = viewChild.required<NgForm>('videoForm');
  readonly videoSaved = output<Video>();
  readonly videoDraftSaved = output<void>();
  readonly formDirtyChange = output<boolean>();
  readonly draftSaved = output<string>();
  readonly videoDeleted = output<string>();
  readonly closeDrawer = output<void>();
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set editingVideo(value: Video | null) {
    console.log('Received editing video:', value);
    if (value) {
      this.video.set({ ...value });
      this.isVideoSaved = true;
      this.editingVideoId = value.id || null;
      
      // Check if video is trimmed by looking at the path or the isTrimmed flag
      const isTrimmed = value.storagePath?.includes('/edited/') || value.isTrimmed === true;
      this.isVideoTrimmed.set(isTrimmed);
      console.log('Video trimmed state set to:', isTrimmed);
      
      // If we have a storagePath, construct the full URL
      if (value.storagePath) {
        console.log('Using storage path:', value.storagePath);
        const fullUrl = environment.videoBaseUrl + encodeURIComponent(value.storagePath) + environment.videoUrlSuffix;
        console.log('Constructed video URL:', fullUrl);
        this.videoSource.set(fullUrl);
      } else if (value.videoUrl) {
        console.log('Using video URL:', value.videoUrl);
        this.videoSource.set(value.videoUrl);
      }

      // Load the video as blob to ensure we have all sources set up
      this.loadVideoAsBlob();
    } else {
      this.resetForm();
    }
  }
  readonly recordingModal = viewChild<ElementRef<HTMLDialogElement>>('recordingModal');
  readonly videoEditModal = viewChild.required<ElementRef>('videoEditModal');
  readonly deleteConfirmModal = viewChild.required<ElementRef>('deleteConfirmModal');

  readonly videoEditComponent = viewChild<VideoEditComponent>('videoEditComponent');


  debugInfo = signal<string>('');
  formDirty = signal(false);
  isUploading = signal(false);
  uploadProgress = computed(() => this.recordingService.getUploadProgress());
  uploadedFile = signal<{ filename: string, url: string } | null>(null);
  dynamicFields = signal<DynamicField[]>([]);

  video = signal<Video>({
    patientFirstName: '',
    patientLastName: '',
    patientDateOfBirth: null,
    description: '',
    status: 'draft',
    createdAt: new Date(),
    userId: '',
    priorityScore: 0,
    additionalInfo: {
      symptoms: {},
      conditions: {}
    }
  });

  editingVideoId: string | null = null;
  currentDateTime = signal(new Date().toLocaleString());
  savedVideoUrl = signal<string | null>(null);
  savedVideoFilename = signal<string | null>(null);
  private videoFilename: string | null = null;
  currentVideo: Video | null = null;
  isVideoSaved = false;
  localVideoUrl: string | null = null;
  showDeleteSuccessAlert = false;
  private _videoBlob: Blob | null = null;
  videoBlobUrl = signal<string | null>(null);
  private initialFormValue: any;
  webcamStarted = false;
  private isClosing = false;
  showTrimmedAlert: boolean = false;
  trimmedDuration: number = 0;


  readonly videoTrimmed = output<string>();

  readonly readOnly = input<boolean>(false);

  readonly webcamComponent = viewChild<WebcamComponent>('webcamComponent');

  videoSource = signal<string | null>(null);

  formValid = signal(false);
  incompleteFields = signal<string[]>([]);

  canSendVideo = computed(() => {
    const incompleteFields = this.updateIncompleteFields();
    const hasVideo = !!(this.video().videoUrl || this._videoBlob || (this.editingVideo && this.editingVideo.videoUrl) || this.videoSource());
    
    // Check if all required checkboxes are selected
    const allCheckboxesValid = this.dynamicFields()
      .filter(field => field.type === 'checkbox' && field.required && field.allRequired)
      .every(field => {
        const selectedOptions = this.getSelectedCheckboxOptions(field.id);
        return selectedOptions && selectedOptions.length === field.options?.length;
      });
    
    // Check if both consent fields are filled
    const videoConsent = this.video().additionalInfo.videoConsent;
    const dataConsent = this.video().additionalInfo.dataConsent;
    const bothConsentsProvided = !!videoConsent && !!dataConsent;
    
    return incompleteFields.length === 0 && hasVideo && allCheckboxesValid && bothConsentsProvided;
  });

  buttonLabel = computed(() => {
    return this.shouldShowSendButton() ? 'Send' : 'Update';
  });

  // Update the hasExistingVideo computed property
  hasExistingVideo = computed(() => {
    const video = this.video();
    const result = !!(
      this.videoSource() || 
      video.storagePath ||
      video.videoUrl
    );
    console.log('hasExistingVideo:', result, {
      videoSource: this.videoSource(),
      storagePath: video.storagePath,
      videoUrl: video.videoUrl
    });
    return result;
  });

  // Update the recordingButtonLabel computed property
  recordingButtonLabel = computed(() => "START RECORDING");

  // Update the recordingButtonClasses computed property
  recordingButtonClasses = computed(() => {
    return this.hasExistingVideo() ? "btn btn-error" : "btn btn-primary";
  });

  private platformId = inject(PLATFORM_ID);

  videoDuration = signal(0);
  currentVideoTime = signal(0);
  private _videoUrlSignal = signal<string | null>(null);

  // Replace the videoElement viewChild declaration
  readonly videoElement = viewChild<ElementRef<HTMLVideoElement>>('videoElement');

  // Update the videoUrlGetter
  get videoUrlGetter(): string | null {
    return this._videoUrlSignal() || (this.editingVideo?.videoUrl ?? this.localVideoUrl);
  }

  set videoUrlSetter(value: string | null) {
    this._videoUrlSignal.set(value);
  }

  videoFile: File | null = null;
  gifUrl: string | null = null;

  // Add these new properties
  workerReady = false;
  ffmpegStatus = 'Loading FFmpeg...';

  // Add this with the other signal declarations at the top of the class
  webcamModalOverride = signal<boolean>(false);

  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);

  // Add isDestroyed property with other class properties
  private isDestroyed = false;

  private subscriptions: Subscription[] = [];

  // Add this with other signals
  isVideoTrimmed = signal<boolean>(false);

  constructor(
    private firestore: Firestore,
    private storage: Storage,
    private userService: UserService,
    private toastService: ToastService,
    private mailgunService: MailgunService,
    private storageService: StorageService,
    private emailService: EmailService,
    private recordingService: RecordingService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private deleteRunwayService: DeleteRunwayService,
    private videoTrimService: VideoTrimService
  ) {
    console.log('MailgunService instance:', this.mailgunService);

    // Monitor video source changes
    effect(() => {
      if (this.videoSource()) {
        console.log('Video source changed, updating incomplete fields');
        queueMicrotask(() => {
          this.updateIncompleteFieldsSignal();
        });
      }
    });

    runInInjectionContext(this.injector, () => {
      const configRef = doc(this.firestore, 'config/root');
      onSnapshot(configRef, (snapshot) => {
        const data = snapshot.data();
        if (data && 'webcamModalOverride' in data) {
          this.webcamModalOverride.set(data['webcamModalOverride']);
        }
      });
    });
  }

  ngOnInit() {
    console.log('VideoDetailsComponent initialized');
    if (this.editingVideoId) {
      console.log(`Editing existing video with ID: ${this.editingVideoId}`);
    } else {
      console.log('Creating a new video');
    }
    this.initialFormValue = this.getFormValue();
    this.setupFormChangeDetection();

    this.recordingService.getRecordedBlob().subscribe(blob => {
      if (blob) {
        this.videoBlob = blob;
        this.updateVideoUrl(URL.createObjectURL(blob));
        this.isVideoSaved = true;
      }
    });

    this.loadDynamicFields();

    // Check if there's an existing video URL and load it
    if (this.video()?.videoUrl) {
      this.loadVideoAsBlob();
    }

    this.setupFormValidation();
    this.setupFormChangeListener();
    this.updateIncompleteFieldsSignal(); // Initial check

    // Handle URL parameters using change detection
    this.route.queryParams.subscribe(params => {
      const videoId = params['videoId'];
      const state = params['state'];

      if (videoId) {
        // Load the video details
        this.loadVideoDetails(videoId);
      }

      if (state === 'readyToRecord') {
        // Trigger the recording modal to open
        this.cdr.detectChanges();
        this.openRecordingModal();
      }
    });

    const editingVideoValue = this.editingVideo;
    if (editingVideoValue?.videoUrl) {
      this._videoUrlSignal.set(editingVideoValue.videoUrl);
    }

    this.videoTrimService.getMessages().subscribe(message => {
      if (message.type === 'ready') {
        this.workerReady = true;
        this.ffmpegStatus = 'FFmpeg loaded successfully';
      } else if (message.type === 'error') {
        this.ffmpegStatus = 'Error loading FFmpeg: ' + message.message;
      }
    });

    // Add subscription tracking with null checks
    const formChanges = this.videoForm()?.valueChanges;
    if (formChanges) {
      this.subscriptions.push(
        formChanges.subscribe(() => {
          if (!this.isDestroyed) {
            this.checkFormDirty();
          }
        })
      );
    }

    if (this.videoForm()) {
      Object.keys(this.videoForm().form.controls).forEach(key => {
        const control = this.videoForm().form.get(key);
        if (control?.valueChanges) {
          this.subscriptions.push(
            control.valueChanges.subscribe(() => {
              if (!this.isDestroyed && control.touched) {
                this.setFormDirty(true);
              }
            })
          );
        }
      });
    }
  }

  ngAfterViewInit() {
    // Setup the effect after the view is initialized
    runInInjectionContext(this.injector, () => {
      effect(() => {
        const videoEditComponent = this.videoEditComponent();
        if (videoEditComponent) {
          // Handle video edit component when available
          this.updateVideoEditComponent();
        }
      });
    });
  }

  private updateVideoEditComponent() {
    console.group('VideoDetailsComponent: updateVideoEditComponent');
    console.log('Video URL:', this.videoSource());
    console.log('Video Duration:', this.videoDuration());
    console.log('Current Time:', this.currentVideoTime());
    
    const videoEditComponent = this.videoEditComponent();
    if (videoEditComponent) {
      // Update the VideoEditComponent with the current video source
      const currentVideoSource = this.videoSource();
      if (currentVideoSource) {
        videoEditComponent.updateUrl(currentVideoSource);
      }
      
      // Check if we need to update the duration from the video element
      const currentVideoDuration = this.videoDuration();
      if (currentVideoDuration === 0 && this.isVideoTrimmed()) {
        // If we've trimmed the video but don't have a duration yet,
        // get it directly from the VideoEditComponent
        const actualDuration = videoEditComponent.getDuration();
        if (actualDuration > 0) {
          console.log('Updating duration from VideoEditComponent:', actualDuration);
          this.videoDuration.set(actualDuration);
          videoEditComponent.updateDuration(actualDuration);
        } else {
          // If we still don't have a duration, schedule another check
          setTimeout(() => {
            const newDuration = videoEditComponent.getDuration();
            if (newDuration > 0) {
              console.log('Updated duration after delay:', newDuration);
              this.videoDuration.set(newDuration);
              videoEditComponent.updateDuration(newDuration);
              this.cdr.detectChanges();
            }
          }, 300);
        }
      } else if (currentVideoDuration > 0) {
        // Normal case - update with our current duration
        videoEditComponent.updateDuration(currentVideoDuration);
      }
      
      // Update the VideoEditComponent with the current video time
      const currentVideoTime = this.currentVideoTime();
      if (currentVideoTime >= 0) {
        videoEditComponent.updateTime(currentVideoTime);
      }
      
      // If we have the original filename, pass it to the VideoEditComponent
      const currentVideo = this.video();
      if (currentVideo && currentVideo.filename) {
        // We can't directly set the input signal, so we'll need to find another way
        // to pass the filename to the VideoEditComponent
        console.log('Original filename:', currentVideo.filename);
        // The VideoEditComponent will get the filename from its input binding
      }
    }
    
    console.groupEnd();
  }

  async loadVideoDetails(videoId: string) {
    console.log('Loading video details for ID:', videoId);
    return runInInjectionContext(this.injector, async () => {
      try {
        const videoRef = doc(this.firestore, 'videos', videoId);
        const videoDoc = await getDoc(videoRef);
        
        if (videoDoc.exists()) {
          const videoData = videoDoc.data() as Video;
          videoData.id = videoDoc.id;
          
          // Update the component state
          this.video.set(videoData);
          this.editingVideoId = videoId;
          
          // If we have a blob URL and we're in production, we need to get the actual storage URL
          if (videoData.videoUrl?.startsWith('blob:') && !videoData.videoUrl.includes('localhost')) {
            console.log('Found blob URL in production, fetching actual storage URL');
            // Query the recordings collection for this user's most recent recording
            const recordingsRef = collection(this.firestore, 'recordings');
            const q = query(recordingsRef, 
              where('userId', '==', videoData.userId),
              orderBy('createdAt', 'desc'),
              limit(1)
            );
            
            const recordingsSnapshot = await getDocs(q);
            if (!recordingsSnapshot.empty) {
              const recordingDoc = recordingsSnapshot.docs[0];
              const recordingData = recordingDoc.data();
              if (recordingData['downloadURL']) {
                console.log('Found storage URL:', recordingData['downloadURL']);
                // Update the video document with the correct URL
                await updateDoc(videoRef, {
                  videoUrl: recordingData['downloadURL'],
                  storagePath: recordingData['filename']
                });
                
                // Update our local video data
                videoData.videoUrl = recordingData['downloadURL'];
                videoData.storagePath = recordingData['filename'];
                this.video.set(videoData);
                
                // Set the video source
                this.videoSource.set(recordingData['downloadURL']);
                console.log('Video source set to storage URL:', recordingData['downloadURL']);
              }
            }
          } else if (videoData.storagePath) {
            console.log('Using storage path:', videoData.storagePath);
            const fullUrl = environment.videoBaseUrl + encodeURIComponent(videoData.storagePath) + environment.videoUrlSuffix;
            console.log('Constructed video URL:', fullUrl);
            this.videoSource.set(fullUrl);
          } else if (videoData.videoUrl) {
            console.log('Using video URL:', videoData.videoUrl);
            this.videoSource.set(videoData.videoUrl);
          }
          
          // Check if video is trimmed
          const isTrimmed = videoData.storagePath?.includes('/edited/') || videoData.isTrimmed === true;
          this.isVideoTrimmed.set(isTrimmed);
          
          console.log('Video details loaded:', videoData);
          console.log('Video source set to:', this.videoSource());

          // Load the video as blob to ensure we have all sources set up
          await this.loadVideoAsBlob();
        } else {
          console.warn('Video document not found:', videoId);
          
          // Check if this might be a recording ID instead of a video ID
          const recordingRef = doc(this.firestore, 'recordings', videoId);
          const recordingDoc = await getDoc(recordingRef);
          
          if (recordingDoc.exists()) {
            console.log('Found recording document instead of video document');
            const recordingData = recordingDoc.data();
            
            // Check if this recording has a videoId
            if (recordingData['videoId']) {
              console.log('Recording has videoId:', recordingData['videoId']);
              // Redirect to the video document
              this.router.navigate(['/videos'], { 
                queryParams: { videoId: recordingData['videoId'] } 
              });
              return;
            }
            
            // No videoId, we need to create a video document for this recording
            console.log('Creating video document for recording:', videoId);
            
            // Get user details
            const userId = recordingData['userId'];
            const userDoc = await getDoc(doc(this.firestore, 'users', userId));
            let userData = {};
            
            if (userDoc.exists()) {
              const user = userDoc.data();
              userData = {
                uploader: user['firstName'] && user['lastName'] ? `${user['firstName']} ${user['lastName']}`.trim() : '',
                uploaderEmail: user['email'] || user['workEmail'] || '',
                uploaderOccupation: user['occupation'] || '',
                uploaderOrganization: user['organizationName'] || ''
              };
            }
            
            // Create a new video document
            const newVideoData = {
              userId,
              videoUrl: recordingData['downloadURL'],
              storagePath: recordingData['filename'] || recordingData['storagePath'],
              recordingId: videoId,
              createdAt: recordingData['createdAt'] || new Date(),
              status: 'draft',
              patientFirstName: '',
              patientLastName: '',
              patientDateOfBirth: null,
              description: '',
              priorityScore: 0,
              additionalInfo: {},
              ...userData
            };
            
            const newVideoRef = await addDoc(collection(this.firestore, 'videos'), newVideoData);
            const newVideoId = newVideoRef.id;
            await updateDoc(newVideoRef, { id: newVideoId });
            console.log('Created new video document:', newVideoId);
            
            // Update the recording document with the video ID
            await updateDoc(recordingRef, { videoId: newVideoId });
            console.log('Updated recording document with video ID:', newVideoId);
            
            // Set the video data
            const videoData = newVideoData as Video;
            videoData.id = newVideoId;
            this.video.set(videoData);
            this.editingVideoId = newVideoId;
            
            // Set the video source
            if (recordingData['downloadURL']) {
              this.videoSource.set(recordingData['downloadURL']);
              console.log('Video source set to:', recordingData['downloadURL']);
            }
            
            // Load the video as blob
            await this.loadVideoAsBlob();
            
            // Update the URL to reflect the new video ID
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { videoId: newVideoId },
              replaceUrl: true
            });
          } else {
            this.toastService.show('Video not found', 'error');
          }
        }
      } catch (error) {
        console.error('Error loading video details:', error);
        this.toastService.show('Error loading video', 'error');
      }
    });
  }

  private async loadDynamicFields() {
    this.isLoadingFields = true;
    try {
      await runInInjectionContext(this.injector, async () => {
        const formsCollection = collection(this.firestore, 'forms');
        const videoDetailsDoc = doc(formsCollection, 'videoDetails');
        
        const docSnap = await getDoc(videoDetailsDoc);
        if (docSnap.exists()) {
          const data = docSnap.data();
          this.dynamicFields.set(data?.['fields'] || []);
          this.updateFormControls(data?.['fields'] || []);
        } else {
          this.dynamicFields.set([]);
        }
      });
    } catch (error) {
      console.error('Error loading dynamic fields:', error);
      this.toastService.show('Error loading form fields', 'error');
    } finally {
      this.isLoadingFields = false;
    }
  }

  private updateFormControls(fields: DynamicField[]) {
    if (this.videoForm()) {
      fields.forEach(field => {
        if (field.required) {
          this.videoForm().form.addControl(field.id, new FormControl('', Validators.required));
        } else {
          this.videoForm().form.addControl(field.id, new FormControl(''));
        }
      });
    }
  }

  private updateVideoUrl(url: string) {
    this.video.update(currentVideo => ({
      ...currentVideo,
      videoUrl: url
    }));
  }

  private setupFormChangeDetection() {
    const videoForm = this.videoForm();
    if (videoForm) {
      const formChanges = videoForm.valueChanges;
      if (formChanges) {
        this.subscriptions.push(
          formChanges.subscribe(() => {
            if (!this.isDestroyed) {
              this.checkFormDirty();
            }
          })
        );
      }

      // Add touch detection for each form control
      Object.keys(videoForm.form.controls).forEach(key => {
        const control = videoForm.form.get(key);
        if (control?.valueChanges) {
          this.subscriptions.push(
            control.valueChanges.subscribe(() => {
              if (!this.isDestroyed && control.touched) {
                console.log(`Form field '${key}' touched. Form is now dirty.`);
                this.setFormDirty(true);
              }
            })
          );
        }
      });
    }
  }

  private setupFormValidation() {
    const videoForm = this.videoForm();
    if (videoForm) {
      const subscription = videoForm.form.statusChanges.subscribe((status) => {
        this.formValid.set(status === 'VALID');
      });
      
      this.destroyRef.onDestroy(() => {
        subscription.unsubscribe();
      });
    }
  }
  private setupFormChangeListener() {
    const videoForm = this.videoForm();
    if (videoForm?.valueChanges) {
      const subscription = videoForm.valueChanges.subscribe(() => {
        this.updateIncompleteFieldsSignal();
      });

      this.destroyRef.onDestroy(() => {
        subscription.unsubscribe();
      });
    }
  }

  private updateIncompleteFields(): string[] {
    const incomplete: string[] = [];

    // Check static fields
    if (!this.video().patientFirstName?.trim()) incomplete.push('Patient First Name');
    if (!this.video().patientLastName?.trim()) incomplete.push('Patient Last Name');
    if (!this.video().patientDateOfBirth) incomplete.push('Patient Date of Birth');

    // Check dynamic fields
    this.dynamicFields().forEach(field => {
      if (field.required) {
        if (field.type === 'checkbox' && field.allRequired) {
          // For checkbox fields with allRequired, check if all options are selected
          const selectedOptions = this.getSelectedCheckboxOptions(field.id || '');
          if (!selectedOptions || selectedOptions.length !== field.options?.length) {
            incomplete.push(field.label);
          }
        } else if (!this.video().additionalInfo[field.id || '']) {
          incomplete.push(field.label);
        }
      }
    });

    // Update video presence check to include videoSource
    const hasVideo = !!(
      this.video().videoUrl || 
      this._videoBlob || 
      (this.editingVideo && this.editingVideo.videoUrl) ||
      this.videoSource()
    );

    if (!hasVideo) {
      incomplete.push('Video Recording');
    }
    
    return incomplete;
  }

  updateIncompleteFieldsSignal() {
    this.incompleteFields.set(this.updateIncompleteFields());
  }

  private checkFormDirty() {
    if (this.isDestroyed) return;
    
    const currentValue = this.getFormValue();
    const isDirty = !this.isEqual(this.initialFormValue, currentValue);
    this.setFormDirty(isDirty);
  }

  private getFormValue(): any {
    return {
      patientFirstName: this.video().patientFirstName,
      patientLastName: this.video().patientLastName,
      patientDateOfBirth: this.video().patientDateOfBirth,
      description: this.video().description,
      priorityScore: this.video().priorityScore,
      additionalInfo: JSON.parse(JSON.stringify(this.video().additionalInfo))
    };
  }

  private isEqual(obj1: any, obj2: any): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }

  updateCurrentDateTime() {
    this.currentDateTime.set(new Date().toLocaleString());
  }


  async saveWithoutSending(): Promise<void> {
    if (this.formDirty()) {
      try {
        const user = this.userService.user();
        if (!user) {
          throw new Error('User not logged in');
        }

        const videoData: Partial<Video> = {
          id: this.editingVideoId || undefined,
          patientFirstName: this.video().patientFirstName || '',
          patientLastName: this.video().patientLastName || '',
          patientDateOfBirth: this.video().patientDateOfBirth || null,
          description: this.video().description || '',
          status: 'draft',
          userId: user.uid,
          priorityScore: this.video().priorityScore || 2.5,
          createdAt: new Date(),
          uploader: this.video().uploader || user.displayName || '',
          uploaderOccupation: this.video().uploaderOccupation || '',
          uploaderEmail: this.video().uploaderEmail || user.email || '',
          uploaderOrganization: this.video().uploaderOrganization || '',
          additionalInfo: this.video().additionalInfo || { symptoms: {}, conditions: {} }
        };

        const savedUrl = this.savedVideoUrl();
        if (savedUrl !== null) {
          videoData.videoUrl = savedUrl;
        }

        await runInInjectionContext(this.injector, async () => {
          if (this.editingVideoId) {
            const videoRef = doc(this.firestore, 'videos', this.editingVideoId);
            const existingDoc = await getDoc(videoRef);
            if (existingDoc.exists()) {
              const existingData = existingDoc.data() as Video;
              videoData.createdAt = existingData.createdAt;
              await updateDoc(videoRef, videoData);
            } else {
              console.error('Video document not found');
            }
          } else {
            const docRef = await addDoc(collection(this.firestore, 'videos'), videoData);
            videoData.id = docRef.id;
          }
        });

        this.draftSaved.emit('Draft saved successfully');
        this.videoDraftSaved.emit();
        this.closeModalEvent.emit();
      } catch (error) {
        console.error('Error saving video:', error);
        this.toastService.show('Error saving draft', 'error');
      }
    }
  }

  private async fixBlobUrlInFirestore(videoId: string, userId: string): Promise<void> {
    try {
      await runInInjectionContext(this.injector, async () => {
        // Construct the expected storage path based on the timestamp
        const timestamp = Date.now();
        const expectedStoragePath = `recordings/webcam/${userId}/${timestamp}.mp4`;
        const storageRef = ref(this.storage, expectedStoragePath);
        
        // Get the download URL
        const downloadURL = await getDownloadURL(storageRef);

        // Update the Firestore document
        const videoRef = doc(this.firestore, 'videos', videoId);
        await updateDoc(videoRef, {
          videoUrl: downloadURL,
          storagePath: expectedStoragePath,
          filename: `${timestamp}.mp4`
        });

        // Update local video object
        this.video.update(v => ({
          ...v,
          videoUrl: downloadURL,
          storagePath: expectedStoragePath,
          filename: `${timestamp}.mp4`
        }));

        // Update video source
        this.videoSource.set(downloadURL);
      });
    } catch (error) {
      console.error('Error fixing blob URL:', error);
      throw error;
    }
  }

  private async saveVideoMetadata(videoData: Partial<Video>): Promise<DocumentReference> {
    return runInInjectionContext(this.injector, async () => {
      const videosCollection = collection(this.firestore, 'videos');
      if (videoData.id) {
        const docRef = doc(videosCollection, videoData.id);
        await setDoc(docRef, videoData, { merge: true });
        return docRef;
      } else {
        return await addDoc(videosCollection, videoData);
      }
    });
  }

  async loadVideoAsBlob() {
    const video = this.video();
    if (!video) {
      console.warn('No video data available');
      return;
    }

    console.log('Loading video with data:', {
      storagePath: video.storagePath,
      status: video.status,
      videoUrl: video.videoUrl
    });

    // First priority: use storagePath if available
    if (video.storagePath) {
      console.log('Using storage path:', video.storagePath);
      const fullUrl = environment.videoBaseUrl + encodeURIComponent(video.storagePath) + environment.videoUrlSuffix;
      console.log('Constructed video URL:', fullUrl);
      this.videoSource.set(fullUrl);
      
      // Check if video is trimmed by looking at the path
      const isTrimmed = video.storagePath.includes('/edited/') || video.isTrimmed === true;
      this.isVideoTrimmed.set(isTrimmed);
      console.log('Video trimmed state:', isTrimmed);
      return;
    }

    // If we have a videoUrl but no storagePath, use the videoUrl
    if (video.videoUrl) {
      console.log('Using video URL:', video.videoUrl);
      this.videoSource.set(video.videoUrl);
      return;
    }

    console.warn('No valid video source available');
    this.videoSource.set(null);
  }




  private async uploadVideo(blob: Blob): Promise<string> {
    const filename = `video_${Date.now()}.webm`;
    const storageRef = ref(this.storage, `videos/${filename}`);
    await uploadBytes(storageRef, blob);
    return await getDownloadURL(storageRef);
  }

  onVideoSaved(videoData: { downloadURL: string; filename: string; }) {
    console.log('VideoDetailsComponent: Received video data', videoData);
    this.isVideoSaved = true;
    
    // Run in injection context to fetch the recordingId
    runInInjectionContext(this.injector, async () => {
      try {
        // Get the recordingId by querying the recordings collection for this file
        const recordingsRef = collection(this.firestore, 'recordings');
        const q = query(
          recordingsRef,
          where('filename', '==', videoData.filename),
          orderBy('createdAt', 'desc'),
          limit(1)
        );
        
        const recordingsSnapshot = await getDocs(q);
        let recordingId: string | undefined;
        
        if (!recordingsSnapshot.empty) {
          const recordingDoc = recordingsSnapshot.docs[0];
          recordingId = recordingDoc.id;
          
          // Now query for the video document linked to this recording
          const videosRef = collection(this.firestore, 'videos');
          const videoQuery = query(
            videosRef,
            where('recordingId', '==', recordingId),
            limit(1)
          );
          
          const videosSnapshot = await getDocs(videoQuery);
          if (!videosSnapshot.empty) {
            // This recording already has a video document
            const videoDoc = videosSnapshot.docs[0];
            console.log('🔍 Found existing video document for this recording:', videoDoc.id);
            
            // Set the editingVideoId to link to the existing video
            this.editingVideoId = videoDoc.id;
          }
        }
        
        // Update the video object with the recording info
        this.video.update(v => ({
          ...v,
          videoUrl: videoData.downloadURL,
          filename: videoData.filename,
          recordingId: recordingId // <-- Store the recordingId in the video object
        }));
        
        console.log('Video saved in VideoDetailsComponent', this.video());
        
        // Set the video URL before closing the modal
        this._videoUrlSignal.set(videoData.downloadURL);
        this.videoSource.set(videoData.downloadURL);
        
        // Update the video source with the new URL
        await this.updateVideoSource(videoData.downloadURL);
        
        // Force a duration check immediately
        this.forceVideoDurationCheck(10, 100); // Try 10 times with 100ms intervals
        
        // Close the recording modal
        this.closeRecordingModal();
      } catch (error) {
        console.error('Error in onVideoSaved:', error);
        
        // Fall back to the original behavior if there's an error
        this.video.update(v => ({
          ...v,
          videoUrl: videoData.downloadURL,
          filename: videoData.filename
        }));
        
        // Set the video URL and source
        this._videoUrlSignal.set(videoData.downloadURL);
        this.videoSource.set(videoData.downloadURL);
        
        // Close the recording modal
        this.closeRecordingModal();
      }
    });
  }
  
  // Add a new method to force duration check with retries
  private forceVideoDurationCheck(maxRetries: number, interval: number, currentRetry = 0): void {
    console.log(`Forcing video duration check (attempt ${currentRetry + 1}/${maxRetries})`);
    
    const videoEditComponent = this.videoEditComponent();
    if (!videoEditComponent) {
      console.log('VideoEditComponent not available yet');
      if (currentRetry < maxRetries) {
        setTimeout(() => this.forceVideoDurationCheck(maxRetries, interval, currentRetry + 1), interval);
      }
      return;
    }
    
    const duration = videoEditComponent.getDuration();
    console.log('Current duration from VideoEditComponent:', duration);
    
    if (duration > 0) {
      console.log('Setting video duration to:', duration);
      this.videoDuration.set(duration);
      videoEditComponent.updateDuration(duration);
      this.cdr.detectChanges();
    } else if (currentRetry < maxRetries) {
      // Try again after the specified interval
      setTimeout(() => this.forceVideoDurationCheck(maxRetries, interval, currentRetry + 1), interval);
    } else {
      console.warn('Failed to get video duration after maximum retries');
      
      // As a last resort, try to get the duration directly from the video element
      const videoElement = videoEditComponent.videoElement();
      if (videoElement && videoElement.nativeElement && videoElement.nativeElement.duration > 0) {
        console.log('Setting video duration from video element:', videoElement.nativeElement.duration);
        this.videoDuration.set(videoElement.nativeElement.duration);
        videoEditComponent.updateDuration(videoElement.nativeElement.duration);
        this.cdr.detectChanges();
      }
    }
  }

  closeRecordingModal(event?: string | Event): void {
    if (this.isClosing) {
      console.log('VideoDetailsComponent: Already closing, ignoring duplicate call');
      return;
    }

    this.isClosing = true;
    console.log('VideoDetailsComponent: Closing recording modal');

    const webcamComponent = this.webcamComponent();
    if (webcamComponent) {
      webcamComponent.stopWebcam();
    }

    const recordingModal = this.recordingModal();
    if (recordingModal?.nativeElement) {
      recordingModal.nativeElement.close();
      if (webcamComponent) {
        webcamComponent.isPreparingForEditing = false;
      }
    }

    this.recordingService.resetRecording();

    // Clear the video element
    const videoEditComponent = this.videoEditComponent();
    if (videoEditComponent) {
      videoEditComponent.clearVideo();
    }

    // Clear videoBlob
    this.videoBlob = null;

    console.log('After clearing, videoBlob:', this._videoBlob, 'videoBlobUrl:', this.videoBlobUrl());
    console.trace('Trace for closeRecordingModal');

    this.isClosing = false;
    this.webcamStarted = false;
  }

  stopRecording(): void {
    const webcamComponent = this.webcamComponent();
    if (webcamComponent) {
      webcamComponent.stopWebcam();
    }
  }

  confirmDelete(): void {
    if (this.video() && this.video().id) {
      this.deleteConfirmModal().nativeElement.showModal();
    } else if (confirm('Are you sure you want to delete this video? This action cannot be undone.')) {
      this.deleteVideo();
    } else {
      this.toastService.show('No video to delete', 'error');
    }
  }

  async deleteVideo(fromConfirmModal: boolean = false): Promise<void> {
    if (!fromConfirmModal && !confirm('Are you sure you want to delete this video? This action cannot be undone.')) {
      return;
    }

    const currentVideo = this.video();
    if (!currentVideo.id) {
      console.error('Cannot delete video: missing id');
      return;
    }

    try {
      await runInInjectionContext(this.injector, async () => {
        // First, update the Firestore document to remove the video-related fields
        const videoRef = doc(this.firestore, 'videos', currentVideo.id!);
        await updateDoc(videoRef, {
          videoUrl: deleteField(),
          filename: deleteField(),
          duration: deleteField(),
          storagePath: deleteField()
        });

        // Only attempt to move to delete runway if it's not a blob URL
        if (currentVideo.videoUrl && !currentVideo.videoUrl.startsWith('blob:')) {
          try {
            await this.deleteRunwayService.moveToDeleteRunway(currentVideo.videoUrl, currentVideo.id);
          } catch (error) {
            // Handle specific error cases
            if ((error instanceof Error && error.message === 'ERR_FILE_NOT_FOUND') || 
                (error instanceof TypeError && error.message.includes('Failed to fetch')) ||
                (error instanceof Error && error.message.includes('Failed to move file'))) {
              console.log('File not found or fetch failed, proceeding with document cleanup', error);
              this.toastService.show('Video file not found, cleaning up references', 'info');
            } else {
              // Log warning but continue with deletion for other errors
              console.warn('Could not move video file to delete runway, but references were removed:', error);
            }
          }
        } else if (currentVideo.videoUrl?.startsWith('blob:')) {
          console.warn('Skipping delete runway for blob URL:', currentVideo.videoUrl);
        } else {
          console.warn('No video file to remove, cleaning up references only');
        }
      });

      // Update the local video object
      this.video.update(v => ({
        ...v,
        videoUrl: undefined,
        filename: undefined,
        duration: undefined,
        storagePath: undefined
      }));

      this.toastService.show('Video deleted successfully', 'success');
      this.videoDeleted.emit(currentVideo.id);
      this.resetVideoProperties();
      this.clearAllUrlParams();
    } catch (error) {
      console.error('Error deleting video:', error);
      this.toastService.show('Error deleting video', 'error');
    }
  }

  private resetVideoProperties(): void {
    this.video.update(currentVideo => ({
      ...currentVideo,
      videoUrl: undefined,
      filename: undefined,
      duration: null
    }));

    this.isVideoSaved = false;
    this.videoSource.set(null);
    if (this.videoBlobUrl()) {
      URL.revokeObjectURL(this.videoBlobUrl()!);
      this.videoBlobUrl.set(null);
    }
    this.videoBlob = null;

    // Reset the video element if available
    const videoEditComponent = this.videoEditComponent();
    if (videoEditComponent) {
      videoEditComponent.clearVideo();
    }

    // Reset video-related signals
    this.videoDuration.set(0);
    this.currentVideoTime.set(0);

    // Trigger change detection
    this.cdr.detectChanges();
  }





  async saveAsDraft(): Promise<void> {
    console.group('🎥 [VideoDetails] saveAsDraft');
    console.log('Current video state:', {
      editingVideoId: this.editingVideoId,
      hasVideoUrl: !!this.video().videoUrl,
      videoSource: this.videoSource(),
      recordingId: this.video().recordingId
    });

    if (this.isDestroyed) return;

    const user = this.userService.user();
    if (!user) {
      console.error('❌ User not logged in');
      this.toastService.show('User not logged in', 'error');
      return;
    }

    try {
      // Get the current video URL from the video source (which will be the edited version if trimmed)
      const currentVideoUrl = this.videoSource() || this.video().videoUrl;
      
      let videoData: Partial<Video> = {
        ...this.video(),
        status: 'draft' as VideoStatus,
        userId: user.uid,
        createdAt: Timestamp.now(),
        videoUrl: currentVideoUrl,
        isTrimmed: this.isVideoTrimmed()
      };

      console.log('📄 Video data being prepared:', {
        recordingId: videoData.recordingId,
        videoUrl: videoData.videoUrl?.substring(0, 50) + '...',
        id: videoData.id
      });

      // If we have a new video blob, upload it
      if (this.videoBlob) {
        console.log('📤 Uploading new video blob');
        videoData.videoUrl = await this.uploadVideo(this.videoBlob);
      }

      // Explicitly remove videoUrl if it's null or undefined
      if (videoData.videoUrl === null || videoData.videoUrl === undefined) {
        console.log('🗑️ Removing empty videoUrl field');
        delete videoData.videoUrl;
      }

      // Check if the current video is from a recent recording by checking for recordingId property
      let videoId = this.editingVideoId;
      let isUpdate = !!this.editingVideoId;
      
      // If we don't have an editing ID, but we have a recordingId in our video data,
      // check if we already have a video document in Firestore with this recordingId
      if (!videoId && videoData.recordingId) {
        console.log('🔍 Checking for existing video with recordingId:', videoData.recordingId);
        const videosCollection = collection(this.firestore, 'videos');
        const q = query(videosCollection, where('recordingId', '==', videoData.recordingId), limit(1));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const existingDoc = querySnapshot.docs[0];
          videoId = existingDoc.id;
          isUpdate = true;
          console.log('🔗 Found existing video document for this recording:', videoId);
          
          // Update our reference to match the found document
          this.editingVideoId = videoId;
          
          // Preserve the creation date from the original document
          if (existingDoc.data()['createdAt']) {
            videoData.createdAt = existingDoc.data()['createdAt'];
          }
        } else {
          console.log('🆕 No existing video document found for recordingId:', videoData.recordingId);
        }
      } else if (!videoId) {
        console.log('⚠️ No recordingId available and no editingVideoId set');
      } else {
        console.log('📝 Using existing editingVideoId:', videoId);
      }

      // Remove the id field if it's undefined
      if (videoData.id === undefined) {
        delete videoData.id;
      }

      // Clean up additionalInfo object
      if (videoData.additionalInfo) {
        videoData.additionalInfo = this.cleanObject(videoData.additionalInfo);
      }

      // Clean the entire videoData object
      videoData = this.cleanObject(videoData);

      console.log('💾 Saving draft with data:', {
        id: videoId || 'new',
        isUpdate,
        ...videoData
      });

      // If we have an ID, use it to update the document
      if (videoId) {
        const docRef = doc(this.firestore, 'videos', videoId);
        await updateDoc(docRef, videoData);
        
        console.log('✅ Video updated as draft with ID:', videoId);
        
        // Set form dirty state to false before showing toast and emitting event
        if (!this.isDestroyed) {
          this.setFormDirty(false);
        }

        this.toastService.show('Video saved as draft', 'success');
        
        if (!this.isDestroyed) {
          this.videoDraftSaved.emit();
        }

        // Update the local video object with the videoId
        this.video.update(v => ({ ...v, id: videoId }));
      } else {
        // Otherwise create a new document
        const docRef = await this.saveVideoMetadata(videoData);
        console.log('✅ Video saved as draft with ID:', docRef.id);

        // Set form dirty state to false before showing toast and emitting event
        if (!this.isDestroyed) {
          this.setFormDirty(false);
        }

        this.toastService.show('Video saved as draft', 'success');
        
        if (!this.isDestroyed) {
          this.videoDraftSaved.emit();
        }

        // Update the local video object with the new ID
        this.video.update(v => ({ ...v, id: docRef.id }));
      }

      // Remove URL parameters
      this.clearAllUrlParams();
    } catch (error) {
      console.error('❌ Error saving video as draft:', error);
      if (!this.isDestroyed) {
        this.toastService.show('Error saving video as draft', 'error');
      }
    }
    console.groupEnd();
  }

  private cleanObject(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }
    
    if (obj instanceof Date || obj instanceof Timestamp) {
      return obj;
    }

    // If it's an array, clean each element
    if (Array.isArray(obj)) {
      return obj.map(item => this.cleanObject(item)).filter(item => item !== undefined);
    }

    // If it's not an object, return as is (unless it's undefined)
    if (typeof obj !== 'object') {
      return obj === undefined ? null : obj;
    }

    const cleanedObj: any = {};
    Object.keys(obj).forEach(key => {
      if (obj[key] === undefined) {
        // Skip undefined values entirely
        return;
      } else if (obj[key] !== null && typeof obj[key] === 'object') {
        // Clean nested objects
        const cleaned = this.cleanObject(obj[key]);
        if (cleaned !== undefined && cleaned !== null) {
          cleanedObj[key] = cleaned;
        }
      } else {
        // Add non-undefined primitive values
        cleanedObj[key] = obj[key];
      }
    });
    return cleanedObj;
  }

  private checkVideoContent() {
    setTimeout(() => {
      const videoEditComponent = this.videoEditComponent();
      if (videoEditComponent) {
        const videoElement = videoEditComponent.videoElement();
        const sourceElement = videoElement?.nativeElement?.querySelector('source');
        const sourceUrl = sourceElement?.src || videoElement?.nativeElement?.src || '';

        let debugMessage = '';

        if (sourceUrl) {
          debugMessage += `Video source found: ${sourceUrl}\n`;
          if (sourceUrl.startsWith('blob:')) {
            debugMessage += 'URL type: Local (Blob URL)\n';
          } else if (sourceUrl.includes('firebasestorage')) {
            debugMessage += 'URL type: Remote (Firebase Storage)\n';
          } else if (sourceUrl.startsWith('http://localhost')) {
            debugMessage += 'URL type: Local (Development Server)\n';
          } else {
            debugMessage += 'URL type: Unknown\n';
          }
        } else {
          debugMessage += 'No video source found\n';
        }

        debugMessage += `video().videoUrl: ${this.video().videoUrl || 'not set'}\n`;
        debugMessage += `localVideoUrl: ${this.localVideoUrl || 'not set'}\n`;

        console.log(debugMessage);
        this.debugInfo.set(debugMessage);
      }
    });
  }

  calculatePatientAge(dateOfBirth: Date | Timestamp | null | undefined): number | null {
    if (!dateOfBirth) {
      return null;
    }

    const dob = dateOfBirth instanceof Timestamp ? dateOfBirth.toDate() : dateOfBirth;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    return age;
  }

  formatDate(date: Date): string {
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Intl.DateTimeFormat('en-GB', options).format(new Date(date));
  }

  lookupOrganization(organization: string) {
    window.open(`https://www.google.com/search?q=${encodeURIComponent(organization)}`, '_blank');
  }

  canSaveWithoutSending(): boolean {
    return !!this.video().patientFirstName && !!this.video().patientLastName;
  }



  onRecordingFinished(event: any) {
    console.log('Recording finished:', event);
    // Handle the finished recording here
    // You might want to update the video object or perform other actions
  }

  resetForm(): void {
    console.log('Resetting form');
    this.video.set({
      patientFirstName: '',
      patientLastName: '',
      patientDateOfBirth: null,
      description: '',
      status: 'draft',
      createdAt: new Date(),
      userId: '',
      priorityScore: 2.5,
      additionalInfo: { symptoms: {}, conditions: {} },
      videoUrl: '',
      filename: undefined
    });
    this.isVideoSaved = false;
    this.savedVideoUrl.set(null);
    this.savedVideoFilename.set(null);
    this.editingVideoId = null;
    this.videoSource.set(null);
    if (this.videoBlobUrl()) {
      URL.revokeObjectURL(this.videoBlobUrl()!);
      this.videoBlobUrl.set(null);
    }
    this.setFormDirty(false);
  }

  closeModal(event?: Event): void {
    console.log('VideoDetailsComponent: closeModal called, form dirty:', this.formDirty());
    if (event) {
      event.preventDefault();
    }

    if (this.formDirty() || this.hasUnsavedLocalVideo()) {
      this.attemptCloseWithUnsavedChanges.emit(true);
    } else {
      this.resetFormAndClose();
    }
    this.clearAllUrlParams();
    this.closeDrawer.emit();
  }

  private hasUnsavedLocalVideo(): boolean {
    return !!this.localVideoUrl && !this.editingVideo?.videoUrl;
  }

  private resetFormAndClose(): void {
    // Reset the form to its initial state
    this.video.set(this.initialFormValue);
    this.formDirty.set(false);

    // Clear local video data
    if (this.localVideoUrl) {
      URL.revokeObjectURL(this.localVideoUrl);
      this.localVideoUrl = null;
    }
    this.videoBlob = null;
    this.isVideoSaved = false;

    // Reset form using existing method
    this.resetForm();

    // Close the drawer/modal
    this.closeModalEvent.emit();
  }

  openRecordingModal(): void {
    console.log('openRecordingModal called, editingVideo:', this.editingVideo);
    this.isVideoSaved = false;
    this.webcamStarted = true;

    if (this.canStartRecording()) {
      console.log('Opening recording modal');
      this.updateUrl('readyToRecord', true);

      // Ensure view is ready using change detection
      this.cdr.detectChanges();

      const recordingModal = this.recordingModal();
      if (!recordingModal?.nativeElement) {
        console.warn('Recording modal not available');
        return;
      }

      console.log('Modal element:', recordingModal.nativeElement);
      
      // Wait for webcam component and modal to be ready
      setTimeout(() => {
        const webcamComponent = this.webcamComponent();
        console.log('Webcam component:', webcamComponent);

        if (webcamComponent) {
          webcamComponent.initializeForRecording();
        } else {
          console.warn('WebcamComponent not found, modal may not function correctly');
        }

        try {
          // First ensure the modal is visible
          recordingModal.nativeElement.style.display = 'block';
          
          // Then try to show it as a modal
          if (recordingModal.nativeElement.showModal) {
            recordingModal.nativeElement.showModal();
          }
          
          // Add modal-open class to body for styling
          document.body.classList.add('modal-open');
        } catch (error) {
          console.warn('Could not show modal dialog, falling back to regular display:', error);
          // Ensure modal is visible even if showModal fails
          recordingModal.nativeElement.style.display = 'block';
          recordingModal.nativeElement.setAttribute('open', '');
        }
      }, 100); // Give a bit more time for DOM to be ready
    } else {
      console.warn('Cannot start recording - conditions not met');
    }
  }

  private updateUrl(param: string | null | undefined, isState: boolean = true) {
    const currentVideoId = this.route.snapshot.queryParams['videoId'];
    const queryParams: { [key: string]: string | null | undefined } = {};

    if (isState) {
      queryParams['videoId'] = currentVideoId;
      queryParams['state'] = param ?? undefined;
    } else {
      queryParams['videoId'] = param ?? undefined;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge'
    });
  }

  private openModal(): void {
    if (isPlatformBrowser(this.platformId)) {
      const recordingModalRef = this.recordingModal();
      if (!recordingModalRef?.nativeElement) {
        console.warn('Recording modal not available');
        return;
      }

      const modal = recordingModalRef.nativeElement;
      if (modal.showModal) {
        modal.showModal();
      } else {
        // Fallback for browsers that don't support showModal (like Safari)
        modal.setAttribute('open', '');
        document.body.classList.add('modal-open');
      }
    }
  }

  onModeSelected(mode: string): void {
    // Implement the logic for mode selection if needed
    console.log('Mode selected:', mode);
  }

  onRecordingStarted(): void {
    // Implement the logic for when recording starts
    console.log('Recording started');
  }

  private stopWebcamStream() {
    setTimeout(() => {
      const webcamComponent = this.webcamComponent();
      if (webcamComponent) {
        webcamComponent.stopWebcamStream();
      }
    }, 300);
  }

  openVideoEditModal() {
    console.log('Local video URL:', this.localVideoUrl);
    console.log('Editing video URL:', this.editingVideo?.videoUrl);
    this.videoEditModal().nativeElement.showModal();
  }

  private getVideoDuration(blob: Blob): Promise<number> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(isFinite(video.duration) ? video.duration : 0);
      };

      video.onerror = (e) => {
        reject('Error loading video metadata');
      };

      video.src = window.URL.createObjectURL(blob);
    });
  }

  async onCroppedVideoSaved(event: { 
    start: number; 
    end: number; 
    url: string; 
    duration: number; 
    filename: string 
  }) {
    console.log('onCroppedVideoSaved called with event:', event);
    try {
      // Update video object with new trimmed video details
      this.video.update(currentVideo => ({
        ...currentVideo,
        videoUrl: event.url,
        duration: event.duration,
        filename: event.filename
      }));

      // Update video element if available
      const videoEditComponent = this.videoEditComponent();
      if (videoEditComponent) {
        videoEditComponent.setVideoSource(event.url);
      }

      this.toastService.show('Video trimmed and saved successfully', 'success');
    } catch (error) {
      console.error('Error handling cropped video:', error);
      this.toastService.show('Error saving trimmed video', 'error');
    }
  }


  private extractFileNameFromUrl(url: string): string {
    const match = url.match(/\/o\/(.+?)\?/);
    if (match && match[1]) {
      return decodeURIComponent(match[1]);
    }
    const urlParts = url.split('/');
    return urlParts[urlParts.length - 1].split('?')[0] || 'unknown';
  }

  private async cropVideo(start: number, end: number, url: string, duration: number): Promise<Blob> {
    console.log('cropVideo called with:', { start, end, url, duration });

    const videoEditComponent = this.videoEditComponent();
    if (!videoEditComponent) {
      console.error('Video element is not available');
      throw new Error('Video element is not available');
    }

    const videoElement = videoEditComponent.videoElement();
    if (!videoElement?.nativeElement) {
      throw new Error('Video element is not available');
    }

    const video = videoElement.nativeElement;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Unable to create canvas context');
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    video.currentTime = start;

    return new Promise((resolve) => {
      video.onseeked = () => {
        const chunks: Blob[] = [];
        const mediaRecorder = new MediaRecorder(canvas.captureStream());

        mediaRecorder.ondataavailable = (e) => {
          chunks.push(e.data);
        };

        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'video/webm' });
          console.log('Cropped video blob created, size:', blob.size);
          resolve(blob);
        };

        mediaRecorder.start();

        const drawFrame = () => {
          if (video.currentTime < end) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            video.currentTime += 1 / 30; // Assume 30 fps
            requestAnimationFrame(drawFrame);
          } else {
            mediaRecorder.stop();
          }
        };

        drawFrame();
      };
    });
  }

  private async updateVideoInDatabase(video: Video) {
    const videosCollection = collection(this.firestore, 'videos');
    const videoDocRef = doc(videosCollection, video.id!);
    await updateDoc(videoDocRef, {
      videoUrl: video.videoUrl,
      duration: video.duration
    });
  }

  hasVideoFile(): boolean {
    return !!(this.editingVideo?.videoUrl || this.localVideoUrl);
  }

  shouldShowSendButton(): boolean {
    return !this.video() || !this.video().id || this.video().status === 'draft';
  }



  canStartRecording(): boolean {
    return !!this.video().patientFirstName &&
      !!this.video().patientLastName;
  }

  canSaveAsDraft(): boolean {
    return this.canStartRecording();
  }

  onFieldChange(fieldName: string): void {
    if (!this.formDirty()) {
      console.log(`Form field '${fieldName}' changed. Form is now dirty.`);
      this.setFormDirty(true);
    }

    // Get the field configuration
    const field = this.dynamicFields().find(f => f.id === fieldName);

    // Get the new value
    let newValue = this.videoForm().form.get(fieldName)?.value;

    // If it's a range input and the value is 0, set it to null
    if (field?.type === 'rating' && newValue === 0) {
      newValue = null;
    }

    // Update the video object with the new value
    this.video.update(currentVideo => ({
      ...currentVideo,
      additionalInfo: {
        ...currentVideo.additionalInfo,
        [fieldName]: newValue
      }
    }));

    this.updateIncompleteFieldsSignal();
    this.cdr.detectChanges();
  }

  private setFormDirty(isDirty: boolean) {
    if (this.isDestroyed) return;
    
    if (this.formDirty() !== isDirty) {
      this.formDirty.set(isDirty);
      console.log('VideoDetailsComponent: Form dirty state changed:', isDirty);
      this.formDirtyChange.emit(isDirty);
    }
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    
    // Clean up subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
    // Clean up form
    const videoForm = this.videoForm();
    if (videoForm) {
      Object.keys(videoForm.form.controls).forEach(key => {
        videoForm.form.removeControl(key);
      });
    }

    // Clean up URLs
    if (this.localVideoUrl) {
      URL.revokeObjectURL(this.localVideoUrl);
    }
    if (this.videoBlobUrl()) {
      URL.revokeObjectURL(this.videoBlobUrl()!);
      this.videoBlobUrl.set(null);
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    if (this.formDirty()) {
      $event.returnValue = true;
    }
  }

  // Update this method to handle video deletion if necessary
  handleStartRecordingClick() {
    try {
      console.log('Start recording clicked');
      console.log('Can start recording:', this.canStartRecording());
      console.log('Is uploading:', this.isUploading());
      
      if (this.canStartRecording() && !this.isUploading()) {
        this.openRecordingModal();
      } else {
        console.warn('Cannot start recording:', {
          canStart: this.canStartRecording(),
          isUploading: this.isUploading()
        });
      }
    } catch (error) {
      console.error('Error in handleStartRecordingClick:', error);
      this.toastService.show('Error starting recording', 'error');
    }
  }

  // Getter for videoBlob
  get videoBlob(): Blob | null {
    return this._videoBlob;
  }

  // Setter for videoBlob
  set videoBlob(value: Blob | null) {
    this._videoBlob = value;
    // Update the videoBlobUrl when videoBlob changes
    if (value) {
      this.videoBlobUrl.set(URL.createObjectURL(value));
    } else {
      if (this.videoBlobUrl()) {
        URL.revokeObjectURL(this.videoBlobUrl()!);
      }
      this.videoBlobUrl.set(null);
    }
  }

  // Add this method to show the incomplete fields alert
  showIncompleteFieldsAlert() {
    const incomplete = this.incompleteFields();
    if (incomplete.length > 0) {
      const fields = incomplete.join(', ');
      this.toastService.show(`Please complete the following fields: ${fields}`, 'error');
    }
  }

  // Update the saveVideo method to check if all required fields are completed before sending
  async saveVideo(status: VideoStatus = 'submitted'): Promise<string | null> {
    console.log('saveVideo method called with status:', status);

    this.setFormDirty(false);
    this.updateIncompleteFieldsSignal();

    if (!this.canSendVideo()) {
      console.log('Cannot send video: incomplete fields or missing video');
      this.toastService.show('Please complete all required fields and provide a video before sending', 'error');
      return null;
    }

    // Get current user details
    const currentUser = await this.userService.getCurrentUser();
    if (!currentUser) {
      this.toastService.show('Please log in to save video', 'error');
      return null;
    }

    // Start with a copy of the current video data
    let videoData = { ...this.video() };
    
    // Prepare the video data
    let videoToSave: Partial<Video> = {
      ...videoData,
      status,
      userId: currentUser.uid,
      uploader: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
      uploaderEmail: currentUser.email || '',
      uploaderOccupation: currentUser.occupation || '',
      uploaderOrganization: currentUser.organizationName || '',
      additionalInfo: {
        ...videoData.additionalInfo,
        eventDuration: videoData.additionalInfo.eventDuration || undefined
      },
      isTrimmed: this.isVideoTrimmed()
    };
    
    // If we have a video blob, upload it to Firebase Storage
    if (this._videoBlob) {
      try {
        console.log('Uploading video blob...');
        const timestamp = Date.now();
        const storagePath = `recordings/webcam/${currentUser.uid}/${timestamp}.webm`;
        const storageRef = ref(this.storage, storagePath);
        
        await uploadBytes(storageRef, this._videoBlob);
        const downloadURL = await getDownloadURL(storageRef);
        
        videoToSave.storagePath = storagePath;
        videoToSave.filename = `${timestamp}.webm`;
        videoToSave.videoUrl = downloadURL;
      } catch (uploadError) {
        console.error('Error uploading video:', uploadError);
        this.toastService.show('Error uploading video', 'error');
        return null;
      }
    }
    
    // Remove ID if it's undefined (will be assigned by Firestore)
    if (videoToSave.id === undefined) {
      delete videoToSave.id;
    }

    // Clean the object to remove undefined values that Firestore rejects
    videoToSave = this.cleanObject(videoToSave);

    console.log('Saving video with data:', videoToSave);

    try {
      console.log('Attempting to save video with status:', status);
      let videoDocRef: DocumentReference;
      if (this.editingVideoId) {
        const videoDoc = doc(this.firestore, 'videos', this.editingVideoId);
        await updateDoc(videoDoc, videoToSave);
        videoDocRef = videoDoc;
      } else {
        videoDocRef = await addDoc(collection(this.firestore, 'videos'), videoToSave);
      }
      this.editingVideoId = videoDocRef.id;
      console.log('Video saved successfully');

      const updatedVideo: Video = {
        ...videoToSave as Video,
        id: this.editingVideoId
      };
      this.video.set(updatedVideo);

      if (status === 'submitted') {
        console.log('Calling sendNewVideoNotification');
        this.emailService.sendNewVideoNotification();
      }

      this.videoSaved.emit(updatedVideo);
      this.toastService.show('Video saved successfully', 'success');
      return videoDocRef.id;

    } catch (error) {
      console.error('Error saving video:', error);
      this.toastService.show('Error saving video', 'error');
      return null;
    } finally {
      this.stopWebcamStream();
      this.removeUrlParams();
      this.setFormDirty(false);
      this.closeModalEvent.emit();
      this.clearAllUrlParams();
    }
  }

  onRecordingDiscarded(): void {
    this.isVideoSaved = false;
    this.videoSource.set(null);
    // The modal will be closed by the closeModal event, so we don't need to call closeRecordingModal here
  }

  onVideoMetadataLoaded(event?: Event) {
    const videoEditComponent = this.videoEditComponent();
    if (videoEditComponent) {
      const newDuration = videoEditComponent.getDuration();
      if (newDuration > 0) {
        console.log('Video metadata loaded - updating duration:', newDuration);
        this.videoDuration.set(newDuration);
        
        // If this is a trimmed video, make sure the VideoEditComponent has the correct duration
        if (this.isVideoTrimmed()) {
          console.log('This is a trimmed video - ensuring VideoEditComponent has correct duration');
          videoEditComponent.updateDuration(newDuration);
        }
      } else {
        // If duration is 0, try again after a short delay
        setTimeout(() => {
          const delayedDuration = videoEditComponent.getDuration();
          if (delayedDuration > 0) {
            console.log('Video duration after delay:', delayedDuration);
            this.videoDuration.set(delayedDuration);
            
            if (this.isVideoTrimmed()) {
              videoEditComponent.updateDuration(delayedDuration);
            }
            
            this.cdr.detectChanges();
          }
        }, 300);
      }
    }
  }

  onVideoTimeUpdate() {
    const videoEditComponent = this.videoEditComponent();
    if (videoEditComponent) {
      this.currentVideoTime.set(videoEditComponent.getCurrentTime());
      console.log('Current video time:', this.currentVideoTime());
    }
  }

  seekVideo(time: number) {
    const videoEditComponent = this.videoEditComponent();
    if (videoEditComponent) {
      videoEditComponent.seekToPosition(time);
    } else {
      console.warn('VideoEditComponent is not available');
    }
  }

  private removeUrlParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private clearAllUrlParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true
    });
  }


  async onVideoTrimmed(event: { url: string, filename: string }) {
    console.log('Video trimmed event received:', event);
    try {
      // Get current user ID
      const userId = this.userService.user()?.uid;
      if (!userId) {
        throw new Error('No user ID available');
      }

      // The filename now contains the full storage path
      const storagePath = event.filename;
      console.log('Using storage path from event:', storagePath);

      // Get a fresh download URL from Firebase Storage
      const storageRef = ref(this.storage, storagePath);
      
      try {
        // Get the full download URL with token
        const downloadURL = await getDownloadURL(storageRef);
        console.log('Got fresh download URL:', downloadURL);

        // Only use emulator URL in development environment
        const finalUrl = environment.production ? downloadURL : downloadURL.replace('firebasestorage.googleapis.com', '127.0.0.1:9199');
        console.log('Final URL to use:', finalUrl);

        // Extract just the filename from the path
        const filenameOnly = storagePath.split('/').pop() || storagePath;

        const updateData = {
          storagePath,
          filename: filenameOnly,
          isTrimmed: true,
          updatedAt: Timestamp.now(),
          videoUrl: downloadURL // Always store the original URL in Firestore
        };

        // Update the video object with trimmed video details
        this.video.update(currentVideo => ({
          ...currentVideo,
          ...updateData
        }));

        // Set trimmed state for UI
        this.isVideoTrimmed.set(true);
        console.log('Set trimmed state to true');

        // If we have a video ID, update Firestore to persist the changes
        if (this.editingVideoId) {
          console.log('Updating Firestore document:', this.editingVideoId);
          const videoRef = doc(this.firestore, 'videos', this.editingVideoId);
          
          console.log('Updating Firestore with data:', updateData);
          await updateDoc(videoRef, updateData);
          console.log('Firestore update completed');
          
          // Verify the update
          const updatedDoc = await getDoc(videoRef);
          const updatedData = updatedDoc.data();
          console.log('Verified Firestore document after update:', updatedData);
          
          // Use the appropriate URL based on environment
          console.log('Setting video source URL:', finalUrl);
          
          // Update the video source with the new URL
          await this.updateVideoSource(finalUrl);
          
          // Reset the video duration to force it to be recalculated from the trimmed video
          this.videoDuration.set(0);
          
          // Update the VideoEditComponent if it exists
          this.updateVideoEditComponent();
          
          // Wait for the video to load and update the duration
          setTimeout(() => {
            const videoEditComponent = this.videoEditComponent();
            if (videoEditComponent) {
              const newDuration = videoEditComponent.getDuration();
              console.log('Updated video duration after trim:', newDuration);
              this.videoDuration.set(newDuration);
              this.cdr.detectChanges();
            }
          }, 500);
          
          this.toastService.show('Video successfully trimmed and saved', 'success');
        }

        // Force change detection to update UI
        this.cdr.detectChanges();
        
        // Emit event for parent components with the final URL
        this.videoTrimmed.emit(finalUrl);
        
        // Show trimmed alert
        this.showTrimmedAlert = true;
        setTimeout(() => {
          this.showTrimmedAlert = false;
          this.cdr.detectChanges();
        }, 5000);
      } catch (storageError) {
        console.error('Error getting download URL:', storageError);
        // If we can't get the download URL, try using the original URL
        await this.updateVideoSource(event.url);
        this.toastService.show('Warning: Using fallback video URL', 'info');
      }
    } catch (error) {
      console.error('Error updating trimmed video:', error);
      this.toastService.show('Error saving trimmed video', 'error');
    }
  }

  showSeedDataMessage(): boolean {
    return !environment.production && 
           this.dynamicFields().length === 0 && 
           !this.isLoadingFields;
  }

  private isLoadingFields = false;

  async importSeedData() {
    try {
      this.isLoadingFields = true;
      
      // Example seed data structure
      const seedData = {
        fields: [
          {
            id: 'symptoms',
            type: 'heading',
            label: 'Symptoms'
          },
          {
            id: 'mainSymptom',
            type: 'textarea',
            label: 'Main Symptom',
            required: true
          },
          {
            id: 'symptomSeverity',
            type: 'rating',
            label: 'Symptom Severity',
            required: true,
            options: ['None', 'Mild', 'Moderate', 'Severe', 'Extreme']
          },
          {
            id: 'conditions',
            type: 'heading',
            label: 'Medical Conditions'
          },
          {
            id: 'existingConditions',
            type: 'textarea',
            label: 'Existing Medical Conditions',
            required: false
          },
          {
            id: 'medications',
            type: 'textarea',
            label: 'Current Medications',
            required: false
          }
        ]
      };

      // Add the seed data to Firestore
      const formsCollection = collection(this.firestore, 'forms');
      await setDoc(doc(formsCollection, 'videoDetails'), seedData);

      this.toastService.show('Seed data imported successfully', 'success');
      
      // Reload the dynamic fields
      await this.loadDynamicFields();
    } catch (error) {
      console.error('Error importing seed data:', error);
      this.toastService.show('Error importing seed data', 'error');
    } finally {
      this.isLoadingFields = false;
    }
  }

  private async updateVideoSource(url: string) {
    console.log('Loading video URL:', url);
    
    try {
      // Remove credentials and cache-control header from fetch request
      const response = await fetch(url, {
        credentials: 'omit', // Change from 'include' to 'omit'
        headers: {
          'Accept': 'video/mp4,video/webm,video/*;q=0.9,*/*;q=0.8'
          // Remove cache-control header
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Update signals
      this.videoSource.set(blobUrl);
      this._videoUrlSignal.set(blobUrl);
      
      // Clean up old blob URL if it exists
      const oldBlobUrl = this.videoBlobUrl();
      if (oldBlobUrl) {
        URL.revokeObjectURL(oldBlobUrl);
      }
      
      // Store the new blob URL
      this.videoBlobUrl.set(blobUrl);
      
      // Update the VideoEditComponent if it exists
      const videoEditComponent = this.videoEditComponent();
      if (videoEditComponent) {
        // First clear the current video to force a complete reload
        videoEditComponent.clearVideo();
        
        // Wait a moment for the video element to clear
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Set the new URL
        videoEditComponent.setVideoSource(blobUrl);
      }
      
      this.cdr.detectChanges();

    } catch (error) {
      console.error('Error updating video source:', error);
      // For production, try direct URL as last resort
      this.videoSource.set(url);
      this._videoUrlSignal.set(url);
      
      // Update the VideoEditComponent if it exists
      const videoEditComponent = this.videoEditComponent();
      if (videoEditComponent) {
        // First clear the current video to force a complete reload
        videoEditComponent.clearVideo();
        
        // Wait a moment for the video element to clear
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Set the new URL
        videoEditComponent.setVideoSource(url);
      }
      
      this.cdr.detectChanges();
    }
  }

  // Single implementation of video metadata loaded handler

  // Add methods for handling checkbox fields
  isCheckboxChecked(fieldId: string, option: string): boolean {
    const selectedOptions = this.getSelectedCheckboxOptions(fieldId);
    return selectedOptions ? selectedOptions.includes(option) : false;
  }

  getSelectedCheckboxOptions(fieldId: string): string[] | undefined {
    const value = this.video().additionalInfo[fieldId];
    if (Array.isArray(value)) {
      return value;
    }
    return undefined;
  }

  onCheckboxChange(fieldId: string, option: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    let selectedOptions = this.getSelectedCheckboxOptions(fieldId) || [];
    
    if (checked && !selectedOptions.includes(option)) {
      selectedOptions = [...selectedOptions, option];
    } else if (!checked && selectedOptions.includes(option)) {
      selectedOptions = selectedOptions.filter(item => item !== option);
    }
    
    this.video.update(currentVideo => ({
      ...currentVideo,
      additionalInfo: {
        ...currentVideo.additionalInfo,
        [fieldId]: selectedOptions
      }
    }));
    
    this.onFieldChange(fieldId);
  }

  // Add a method to fix any issues with the current video
  async fixVideoRecordingIssues(): Promise<void> {
    try {
      this.toastService.show('Fixing video recording issues...', 'info');
      
      // Call the RecordingService to fix all issues
      const result = await this.recordingService.fixAllVideoRecordingIssues();
      
      console.log('Fix result:', result);
      this.toastService.show(`Fixed ${result.fixed} issues with ${result.errors} errors`, 'success');
      
      // If we have a video ID, reload the video details
      if (this.editingVideoId) {
        await this.loadVideoDetails(this.editingVideoId);
      }
    } catch (error) {
      console.error('Error fixing video recording issues:', error);
      this.toastService.show('Error fixing video recording issues', 'error');
    }
  }

  // Add a method to fix blob URL issues specifically for the current video
  async fixCurrentVideoBlobUrl(): Promise<void> {
    if (!this.editingVideoId || !this.video().userId) {
      this.toastService.show('No video selected or missing user ID', 'error');
      return;
    }

    try {
      this.toastService.show('Fixing video URL...', 'info');
      
      const videoData = this.video();
      
      // Check if the video URL is a blob URL
      if (videoData.videoUrl?.startsWith('blob:')) {
        console.log('Found blob URL, attempting to fix:', videoData.videoUrl);
        
        // If we have a recordingId, get the recording document
        if (videoData.recordingId) {
          const recordingDoc = await getDoc(doc(this.firestore, 'recordings', videoData.recordingId));
          
          if (recordingDoc.exists()) {
            const recording = recordingDoc.data();
            
            if (recording['downloadURL']) {
              // Update the video document with the recording URL
              await updateDoc(doc(this.firestore, 'videos', this.editingVideoId), {
                videoUrl: recording['downloadURL'],
                storagePath: recording['storagePath'] || recording['filename']
              });
              
              // Update our local video data
              this.video.update(v => ({
                ...v,
                videoUrl: recording['downloadURL'],
                storagePath: recording['storagePath'] || recording['filename']
              }));
              
              // Set the video source
              this.videoSource.set(recording['downloadURL']);
              
              this.toastService.show('Video URL fixed successfully', 'success');
              return;
            }
          }
        }
        
        // If we couldn't fix it using the recordingId, try to find a matching recording
        const recordingsRef = collection(this.firestore, 'recordings');
        const recordingsQuery = query(
          recordingsRef,
          where('userId', '==', videoData.userId),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        
        const recordingsSnapshot = await getDocs(recordingsQuery);
        
        if (!recordingsSnapshot.empty) {
          // Find the closest recording by timestamp
          interface RecordingData {
            id: string;
            downloadURL?: string;
            storagePath?: string;
            filename?: string;
            createdAt?: any;
          }
          
          let closestRecording: RecordingData | null = null;
          let minTimeDiff = Infinity;
          
          for (const recordingDoc of recordingsSnapshot.docs) {
            const recording = recordingDoc.data();
            
            // Handle different timestamp formats
            let recordingTime: Date;
            if (recording['createdAt'] && typeof recording['createdAt'].toDate === 'function') {
              recordingTime = recording['createdAt'].toDate();
            } else {
              recordingTime = new Date(recording['createdAt']);
            }
            
            let videoTime: Date;
            if (videoData.createdAt && typeof (videoData.createdAt as any).toDate === 'function') {
              videoTime = (videoData.createdAt as any).toDate();
            } else {
              // Handle the case where createdAt is a Date or a string
              if (videoData.createdAt instanceof Date) {
                videoTime = videoData.createdAt;
              } else {
                // Fallback to current time if we can't determine the date
                videoTime = new Date();
              }
            }
            
            const timeDiff = Math.abs(recordingTime.getTime() - videoTime.getTime());
            
            if (timeDiff < minTimeDiff) {
              minTimeDiff = timeDiff;
              closestRecording = { 
                id: recordingDoc.id,
                downloadURL: recording['downloadURL'],
                storagePath: recording['storagePath'],
                filename: recording['filename'],
                createdAt: recording['createdAt']
              };
            }
          }
          
          if (closestRecording && closestRecording.downloadURL && minTimeDiff < 60000) { // Within 1 minute
            // Update the video document with the recording URL
            await updateDoc(doc(this.firestore, 'videos', this.editingVideoId), {
              videoUrl: closestRecording.downloadURL,
              storagePath: closestRecording.storagePath || closestRecording.filename,
              recordingId: closestRecording.id
            });
            
            // Update the recording document with the video ID
            await updateDoc(doc(this.firestore, 'recordings', closestRecording.id), { 
              videoId: this.editingVideoId 
            });
            
            // Update our local video data
            this.video.update(v => ({
              ...v,
              videoUrl: closestRecording.downloadURL,
              storagePath: closestRecording.storagePath || closestRecording.filename,
              recordingId: closestRecording.id
            }));
            
            // Set the video source
            this.videoSource.set(closestRecording.downloadURL);
            
            this.toastService.show('Video URL fixed successfully', 'success');
            return;
          }
        }
        
        this.toastService.show('Could not find a matching recording to fix the blob URL', 'error');
      } else {
        this.toastService.show('Video URL is not a blob URL, no fix needed', 'info');
      }
    } catch (error) {
      console.error('Error fixing blob URL:', error);
      this.toastService.show('Error fixing blob URL', 'error');
    }
  }
}
